<template>
  <HrPageLayout :title="title" :subtitle="subtitle" :icon="icon" :breadcrumb-items="breadcrumbItems">

    <div v-if="!allowed" class="hrmis-no-access">
      <i class="bx bx-lock-alt"></i>
      <h5>Access Restricted</h5>
      <p>You do not have permission to view this module.</p>
    </div>

    <div v-else class="hrmis-page">

      <!-- Toolbar -->
      <div class="hrmis-toolbar">
        <div class="hrmis-toolbar-left">
          <h3 class="hrmis-page-title"><i class="bx bx-list-ul"></i> {{ title }}</h3>
        </div>
        <div class="hrmis-toolbar-right">
          <button class="hrmis-btn hrmis-btn-ghost" @click="loadList"><i class="bx bx-refresh"></i> Refresh</button>
          <button v-if="permissions.manage" class="hrmis-btn hrmis-btn-primary" @click="openForm()">
            <i class="bx bx-plus"></i> {{ addButtonLabel }}
          </button>
        </div>
      </div>

      <!-- Table Card -->
      <div class="hrmis-card">
        <div class="hrmis-card-header">
          <span class="hrmis-card-title"><i class="bx bx-table"></i> Records</span>
          <span class="hrmis-row-count">{{ total }} record{{ total !== 1 ? 's' : '' }}</span>
        </div>

        <div v-if="loading" class="hrmis-loading">
          <div class="hrmis-spinner"></div><span>Loading...</span>
        </div>

        <div v-else-if="!list.length" class="hrmis-empty">
          <i class="bx bx-folder-open"></i>
          <h5>No records found</h5>
          <p>Click "{{ addButtonLabel }}" to create the first entry.</p>
        </div>

        <div v-else class="hrmis-table-wrap">
          <table class="hrmis-table">
            <thead>
              <tr>
                <th>#</th>
                <th v-for="c in columns" :key="c.key">{{ c.label }}</th>
                <th v-if="permissions.manage">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(r, idx) in list" :key="r.id">
                <td class="hrmis-td-num">{{ (page - 1) * limit + idx + 1 }}</td>
                <td v-for="c in columns" :key="c.key">
                  <span v-if="c.key === 'status'" class="hrmis-status-pill" :class="statusClass(cell(r, c))">{{ cell(r, c) }}</span>
                  <span v-else>{{ cell(r, c) }}</span>
                </td>
                <td v-if="permissions.manage">
                  <div class="hrmis-row-actions">
                    <button class="hrmis-action-btn hrmis-action-edit" @click="openForm(r)" title="Edit"><i class="bx bx-edit"></i></button>
                    <button v-if="deleteFunction" class="hrmis-action-btn hrmis-action-delete" @click="deleteRow(r)" title="Delete"><i class="bx bx-trash"></i></button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <div v-if="total > limit" class="hrmis-pagination">
          <span class="hrmis-pagination-info">Page {{ page }} of {{ totalPages }}</span>
          <nav class="hrmis-pages">
            <button class="hrmis-page-btn" :disabled="page <= 1" @click="page--; loadList()"><i class="bx bx-chevron-left"></i></button>
            <button class="hrmis-page-btn" :disabled="page >= totalPages" @click="page++; loadList()"><i class="bx bx-chevron-right"></i></button>
          </nav>
        </div>
      </div>

    </div>

    <!-- Slide-in Form Panel -->
    <Teleport to="body">
      <div class="hrmis-overlay" v-if="showModal" @click.self="showModal = false"></div>
      <div class="hrmis-panel" :class="{ 'hrmis-panel-open': showModal }">
        <div class="hrmis-panel-header">
          <div class="hrmis-panel-title-wrap">
            <div class="hrmis-panel-icon"><i class="bx" :class="form.id ? 'bx-edit' : 'bx-plus-circle'"></i></div>
            <div>
              <h4 class="hrmis-panel-title">{{ form.id ? 'Edit Record' : addButtonLabel }}</h4>
              <p class="hrmis-panel-subtitle">{{ form.id ? 'Update the details below' : 'Fill in the details below' }}</p>
            </div>
          </div>
          <button class="hrmis-panel-close" @click="showModal = false"><i class="bx bx-x"></i></button>
        </div>
        <div class="hrmis-panel-body">
          <div class="hrmis-form-section">
            <div v-for="f in fields" :key="f.key" class="hrmis-field">
              <label class="hrmis-label">{{ f.label }}<span v-if="f.required" class="hrmis-req"> *</span></label>

              <!-- Employee search -->
              <div v-if="f.type === 'employee'">
                <template v-if="employeeInputMode === 'search'">
                  <div class="hrmis-input-icon-wrap">
                    <i class="bx bx-search hrmis-input-icon"></i>
                    <input v-model="employeeSearchText" type="text" class="hrmis-input hrmis-input-icon-pad"
                      placeholder="Search by name, ID or employee number..."
                      :disabled="!!form.id && f.lockOnEdit"
                      @focus="employeeSuggestionsVisible = true"
                      @input="employeeSuggestionsVisible = true">
                  </div>
                  <div v-if="employeeSuggestionsVisible && filteredEmployees.length" class="hrmis-suggestions">
                    <button v-for="emp in filteredEmployees" :key="emp.id" type="button"
                      class="hrmis-suggestion-item" @click="selectEmployee(f.key, emp)">
                      <span class="hrmis-suggestion-name">{{ employeeLabel(emp) }}</span>
                      <small v-if="employeeIdsLine(emp)" class="hrmis-suggestion-meta">{{ employeeIdsLine(emp) }}</small>
                    </button>
                  </div>
                  <div v-if="form[f.key]" class="hrmis-selected-info">
                    <i class="bx bx-check-circle"></i> {{ selectedEmployeeLabel || form[f.key] }}
                  </div>
                </template>
                <select v-else v-model="form[f.key]" class="hrmis-select" :disabled="!!form.id && f.lockOnEdit">
                  <option value="">Select employee</option>
                  <option v-for="emp in employees" :key="emp.id" :value="String(emp.id)">{{ employeeLabel(emp) }}</option>
                </select>
              </div>

              <!-- Department -->
              <select v-else-if="f.type === 'department'" v-model="form[f.key]" class="hrmis-select">
                <option value="">— Select department —</option>
                <option v-for="d in departments" :key="d.id" :value="String(d.id)">{{ d.departmentName }}</option>
              </select>

              <!-- Select -->
              <select v-else-if="f.type === 'select'" v-model="form[f.key]" class="hrmis-select">
                <option v-for="opt in f.options" :key="opt" :value="opt">{{ opt }}</option>
              </select>

              <!-- Textarea -->
              <textarea v-else-if="f.type === 'textarea'" v-model="form[f.key]" class="hrmis-textarea" rows="3" :placeholder="f.placeholder || ''"></textarea>

              <!-- File -->
              <label v-else-if="f.type === 'file'" class="hrmis-file-drop">
                <input type="file" :accept="f.accept || '*/*'" @change="onFileFieldChange($event, f.key)" hidden>
                <i class="bx bx-cloud-upload hrmis-file-icon"></i>
                <span>Click to upload</span>
              </label>

              <!-- Date -->
              <div v-else-if="f.type === 'date'" class="hrmis-input-icon-wrap">
                <i class="bx bx-calendar hrmis-input-icon"></i>
                <input v-model="form[f.key]" type="date" class="hrmis-input hrmis-input-icon-pad">
              </div>

              <!-- Text default -->
              <input v-else v-model="form[f.key]" type="text" class="hrmis-input" :placeholder="f.placeholder || ''">
            </div>
          </div>
        </div>
        <div class="hrmis-panel-footer">
          <button class="hrmis-btn hrmis-btn-ghost" @click="showModal = false"><i class="bx bx-x"></i> Cancel</button>
          <button class="hrmis-btn hrmis-btn-primary" :disabled="saving" @click="saveForm">
            <span v-if="saving" class="hrmis-btn-spinner"></span>
            <i v-else class="bx bx-save"></i>
            {{ saving ? 'Saving...' : 'Save Record' }}
          </button>
        </div>
      </div>
    </Teleport>

  </HrPageLayout>
</template>

<script>
/* eslint-disable */
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { authUrl, execute, getRight } from "@/api";

export default {
  name: "HrmisCrudModule",
  components: { HrPageLayout },
  props: {
    title: { type: String, required: true },
    subtitle: { type: String, default: "" },
    icon: { type: String, default: "📋" },
    sectionBreadcrumb: { type: String, default: "County HRMIS (SR)" },
    listFunction: { type: String, required: true },
    saveFunction: { type: String, required: true },
    deleteFunction: { type: String, default: "" },
    permissionViewCodes: { type: Array, required: true },
    permissionManageCodes: { type: Array, required: true },
    columns: { type: Array, required: true },
    fields: { type: Array, required: true },
    loadEmployees: { type: Boolean, default: false },
    loadDepartments: { type: Boolean, default: false },
    listAppend: { type: Object, default: () => ({}) },
    addButtonLabel: { type: String, default: "Add record" },
    employeeInputMode: { type: String, default: "select" }
  },
  data() {
    return {
      allowed: false,
      list: [],
      total: 0,
      page: 1,
      limit: 50,
      loading: false,
      saving: false,
      showModal: false,
      permissions: { manage: false },
      form: {},
      employees: [],
      departments: [],
      employeeSearchText: "",
      employeeSuggestionsVisible: false
    };
  },
  computed: {
    breadcrumbItems() {
      return [
        { label: "Home", route: "/dashboard" },
        { label: this.sectionBreadcrumb },
        { label: this.title }
      ];
    },
    totalPages() {
      return this.limit > 0 ? Math.ceil(this.total / this.limit) : 0;
    },
    filteredEmployees() {
      const list = Array.isArray(this.employees) ? this.employees : [];
      if (this.employeeKeywordSearch) {
        const q = String(this.employeeSearchText || "").trim();
        return (q ? list : list.slice(0, 50)).slice(0, 80);
      }
      const q = String(this.employeeSearchText || "").trim().toLowerCase();
      if (!q) return list.slice(0, 20);
      return list.filter((e) => this.employeeMatchesQuery(e, q)).slice(0, 40);
    },
    selectedEmployeeLabel() {
      const id = String(this.form?.employee_id || "");
      if (!id) return "";
      const emp = (this.employees || []).find((e) => String(e.id) === id);
      return emp ? this.employeeLabel(emp) : "";
    }
  },
  created() {
    this.allowed = this.permissionViewCodes.some((c) => getRight(c));
    if (!this.allowed) return;
    if (this.loadEmployees) this.fetchEmployees();
    if (this.loadDepartments) this.fetchDepartments();
    this.resetForm();
    this.loadList();
  },
  watch: {
    employeeSearchText(val) {
      if (!this.showModal || this.employeeInputMode !== "search" || !this.employeeKeywordSearch) return;
      if (this._skipEmpSearchWatch) return;
      clearTimeout(this._empKwTimer);
      const kw = typeof val === "string" ? val.trim() : "";
      this._empKwTimer = setTimeout(() => {
        this.fetchEmployees(kw);
      }, 400);
    }
  },
  methods: {
    fetchEmployees(keyword = "") {
      const fd = new FormData();
      fd.append("function", "getEmployees");
      fd.append("status", "Active");
      fd.append("rows_per_page", "2000");
      fd.append("page", "1");
      if (keyword) fd.append("keyword", keyword);
      execute(fd, authUrl).then((res) => {
        const emp =
          res.data && res.data.success && res.data.data && res.data.data.employees
            ? res.data.data.employees
            : [];
        this.employees = Array.isArray(emp) ? emp : [];
      }).catch(() => { this.employees = []; });
    },
    fetchDepartments() {
      const fd = new FormData();
      fd.append("function", "getDepartments");
      execute(fd, authUrl).then((res) => {
        if (res.data && res.data.success && res.data.data && res.data.data.departments)
          this.departments = res.data.data.departments;
      }).catch(() => {});
    },
    resetForm() {
      const o = { id: null };
      this.fields.forEach((f) => {
        o[f.key] = f.type === "file" ? null : (f.default != null ? f.default : "");
      });
      this.form = o;
      this.employeeSearchText = "";
      this.employeeSuggestionsVisible = false;
    },
    employeeLabel(emp) {
      const fn = emp?.firstName || "";
      const ln = emp?.lastName || "";
      const no = emp?.empNo || emp?.personalNumber || "";
      return `${fn} ${ln}`.trim() + (no ? ` (${no})` : "");
    },
    employeeIdsLine(emp) {
      const parts = [];
      if (emp?.nationalId) parts.push(`ID: ${emp.nationalId}`);
      const no = emp?.empNo || emp?.personalNumber;
      if (no) parts.push(`No: ${no}`);
      return parts.join(" · ");
    },
    employeeMatchesQuery(emp, qLower) {
      if (!qLower) return true;
      const norm = (s) => String(s ?? "").toLowerCase().trim();
      const digits = (s) => String(s ?? "").replace(/\D/g, "");
      const qDigits = qLower.replace(/\D/g, "");
      const label = this.employeeLabel(emp).toLowerCase();
      if (label.includes(qLower)) return true;
      const fields = [
        norm(emp.nationalId),
        norm(emp.empNo),
        norm(emp.personalNumber),
        norm(emp.payrollNumber),
        norm(emp.staffNumber)
      ];
      if (fields.some((f) => f && (f.includes(qLower) || qLower.includes(f)))) return true;
      if (qDigits.length >= 3) {
        if (digits(emp.nationalId).includes(qDigits)) return true;
        if (digits(emp.empNo).includes(qDigits)) return true;
      }
      return false;
    },
    selectEmployee(key, emp) {
      this._skipEmpSearchWatch = true;
      this.form[key] = String(emp.id);
      this.employeeSearchText = this.employeeLabel(emp);
      this.employeeSuggestionsVisible = false;
      this.$nextTick(() => {
        this._skipEmpSearchWatch = false;
      });
    },
    onFileFieldChange(event, key) {
      const file = event?.target?.files?.[0] || null;
      this.form[key] = file;
    },
    cell(r, c) {
      const v = r[c.key];
      if (c.truncate && v != null && String(v).length > c.truncate) return String(v).slice(0, c.truncate) + "…";
      if (v === null || v === undefined || v === "") return "–";
      return String(v).slice(0, 50);
    },
    loadList() {
      this.loading = true;
      const fd = new FormData();
      fd.append("function", this.listFunction);
      fd.append("limit", this.limit);
      fd.append("page", this.page);
      Object.keys(this.listAppend || {}).forEach((k) => {
        if (this.listAppend[k] != null && this.listAppend[k] !== "")
          fd.append(k, this.listAppend[k]);
      });
      execute(fd, authUrl)
        .then((res) => {
          this.loading = false;
          const payload = res.data && res.data.success && res.data.data ? res.data.data : {};
          this.list = payload.data || [];
          this.total = payload.total || 0;
          const pm = payload.permissions || {};
          this.permissions = {
            manage: !!pm.manage || this.permissionManageCodes.some((c) => getRight(c))
          };
        })
        .catch(() => {
          this.loading = false;
          this.list = [];
        });
    },
    openForm(row) {
      if (row) {
        const o = { id: row.id };
        this.fields.forEach((f) => {
          let v = row[f.key];
          if (v === null || v === undefined) v = "";
          o[f.key] = f.type === "employee" || f.type === "department" ? String(v) : v;
        });
        this.form = o;
        if (this.employeeInputMode === "search" && o.employee_id) {
          const emp = (this.employees || []).find((e) => String(e.id) === String(o.employee_id));
          this._skipEmpSearchWatch = true;
          this.employeeSearchText = emp ? this.employeeLabel(emp) : "";
          this.$nextTick(() => {
            this._skipEmpSearchWatch = false;
          });
        }
      } else {
        this.resetForm();
        if (this.loadEmployees && this.employeeInputMode === "search" && this.employeeKeywordSearch) {
          this.fetchEmployees("");
        }
      }
      this.showModal = true;
    },
    saveForm() {
      for (const f of this.fields) {
        if (f.required && (!this.form[f.key] && this.form[f.key] !== 0)) {
          alert(`${f.label} is required.`);
          return;
        }
      }
      this.saving = true;
      const fd = new FormData();
      fd.append("function", this.saveFunction);
      if (this.form.id) fd.append("id", this.form.id);
      this.fields.forEach((f) => {
        if (f.key === "id") return;
        if (f.type === "file") {
          const file = this.form[f.key];
          if (file) fd.append(f.key, file);
          return;
        }
        fd.append(f.key, this.form[f.key] == null ? "" : this.form[f.key]);
      });
      execute(fd, authUrl)
        .then((res) => {
          this.saving = false;
          if (res.data && res.data.success) {
            this.showModal = false;
            this.loadList();
          } else {
            alert(res.data && res.data.message ? res.data.message : "Save failed");
          }
        })
        .catch(() => { this.saving = false; });
    },
    statusClass(val) {
      const map = { 'Approved': 'hrmis-status-approved', 'Implemented': 'hrmis-status-approved', 'Pending': 'hrmis-status-pending', 'Draft': 'hrmis-status-draft', 'Rejected': 'hrmis-status-rejected', 'Canceled': 'hrmis-status-rejected' };
      return map[val] || 'hrmis-status-draft';
    },
    deleteRow(row) {
      if (!this.deleteFunction) return;
      const id = row && row.id ? row.id : null;
      if (!id) return;
      if (!confirm("Delete this record? This action cannot be undone.")) return;
      const fd = new FormData();
      fd.append("function", this.deleteFunction);
      fd.append("id", String(id));
      execute(fd, authUrl)
        .then((res) => {
          if (res.data && res.data.success) {
            this.loadList();
          } else {
            alert(res.data && res.data.message ? res.data.message : "Delete failed");
          }
        })
        .catch(() => {});
    }
  }
};
</script>

<style scoped>
@import "@/assets/css/hr-page-shared.css";

.hrmis-no-access { display:flex;flex-direction:column;align-items:center;justify-content:center;padding:80px 20px;color:#94a3b8;text-align:center }
.hrmis-no-access i { font-size:52px;color:#ef4444;margin-bottom:12px }
.hrmis-no-access h5 { font-size:16px;font-weight:700;color:#1e293b;margin:0 0 6px }
.hrmis-no-access p { font-size:13px;margin:0 }

.hrmis-page { display:flex;flex-direction:column;gap:20px }

/* Toolbar */
.hrmis-toolbar { display:flex;align-items:center;justify-content:space-between;background:#fff;border-radius:14px;border:1px solid #e2e8f0;padding:12px 20px;box-shadow:0 2px 8px rgba(0,0,0,.05);flex-wrap:wrap;gap:10px }
.hrmis-page-title { display:flex;align-items:center;gap:8px;font-size:16px;font-weight:700;color:#1e293b;margin:0 }
.hrmis-page-title i { font-size:20px;color:#059669 }
.hrmis-toolbar-right { display:flex;gap:8px }
.hrmis-btn { display:flex;align-items:center;gap:6px;padding:8px 16px;border-radius:8px;border:1px solid transparent;font-size:13px;font-weight:600;cursor:pointer;transition:all .15s;white-space:nowrap }
.hrmis-btn i { font-size:15px }
.hrmis-btn-ghost { background:#f8fafc;color:#475569;border-color:#e2e8f0 }
.hrmis-btn-ghost:hover { background:#e2e8f0 }
.hrmis-btn-primary { background:linear-gradient(135deg,#059669,#0d9488);color:#fff;box-shadow:0 2px 8px rgba(5,150,105,.3) }
.hrmis-btn-primary:hover:not(:disabled) { background:linear-gradient(135deg,#047857,#0f766e);transform:translateY(-1px) }
.hrmis-btn-primary:disabled { opacity:.6;cursor:not-allowed }
.hrmis-btn-spinner { width:13px;height:13px;border:2px solid rgba(255,255,255,.4);border-top-color:#fff;border-radius:50%;animation:hrmis-spin .7s linear infinite;display:inline-block }
@keyframes hrmis-spin { to { transform:rotate(360deg) } }

/* Card */
.hrmis-card { background:#fff;border-radius:14px;border:1px solid #e2e8f0;overflow:hidden;box-shadow:0 2px 8px rgba(0,0,0,.04) }
.hrmis-card-header { display:flex;align-items:center;justify-content:space-between;padding:14px 20px;background:#f8fafc;border-bottom:1px solid #e2e8f0 }
.hrmis-card-title { display:flex;align-items:center;gap:8px;font-size:14px;font-weight:700;color:#1e293b }
.hrmis-card-title i { font-size:18px;color:#059669 }
.hrmis-row-count { font-size:12px;color:#94a3b8;font-weight:600;background:#f1f5f9;padding:3px 10px;border-radius:20px }

/* Loading / Empty */
.hrmis-loading { display:flex;align-items:center;justify-content:center;gap:12px;padding:60px 20px;color:#64748b;font-size:14px }
.hrmis-spinner { width:22px;height:22px;border:3px solid #e2e8f0;border-top-color:#059669;border-radius:50%;animation:hrmis-spin .8s linear infinite }
.hrmis-empty { display:flex;flex-direction:column;align-items:center;justify-content:center;padding:60px 20px;color:#94a3b8;text-align:center }
.hrmis-empty i { font-size:52px;color:#059669;margin-bottom:12px }
.hrmis-empty h5 { font-size:16px;font-weight:700;color:#1e293b;margin:0 0 6px }
.hrmis-empty p { font-size:13px;margin:0 }

/* Table */
.hrmis-table-wrap { overflow-x:auto }
.hrmis-table { width:100%;border-collapse:collapse;font-size:13px }
.hrmis-table th { background:#f8fafc;padding:10px 14px;text-align:left;font-size:11px;font-weight:700;color:#64748b;text-transform:uppercase;letter-spacing:.5px;border-bottom:2px solid #e2e8f0;white-space:nowrap }
.hrmis-table td { padding:12px 14px;border-bottom:1px solid #f1f5f9;color:#334155;vertical-align:middle }
.hrmis-table tr:last-child td { border-bottom:none }
.hrmis-table tr:hover td { background:#f8fafc }
.hrmis-td-num { color:#94a3b8;font-size:12px;font-weight:600;width:40px }

/* Status pills */
.hrmis-status-pill { display:inline-flex;align-items:center;padding:3px 10px;border-radius:20px;font-size:11px;font-weight:700 }
.hrmis-status-approved { background:#f0fdf4;color:#059669 }
.hrmis-status-pending { background:#fffbeb;color:#d97706 }
.hrmis-status-draft { background:#f1f5f9;color:#64748b }
.hrmis-status-rejected { background:#fef2f2;color:#ef4444 }

/* Row actions */
.hrmis-row-actions { display:flex;gap:6px }
.hrmis-action-btn { width:30px;height:30px;border-radius:7px;border:none;display:flex;align-items:center;justify-content:center;font-size:15px;cursor:pointer;transition:all .15s }
.hrmis-action-edit { background:#f0fdf4;color:#059669 }
.hrmis-action-edit:hover { background:#059669;color:#fff }
.hrmis-action-delete { background:#fef2f2;color:#ef4444 }
.hrmis-action-delete:hover { background:#ef4444;color:#fff }

/* Pagination */
.hrmis-pagination { display:flex;align-items:center;justify-content:space-between;padding:14px 20px;border-top:1px solid #f1f5f9 }
.hrmis-pagination-info { font-size:12px;color:#94a3b8 }
.hrmis-pages { display:flex;gap:4px }
.hrmis-page-btn { width:32px;height:32px;border-radius:7px;border:1px solid #e2e8f0;background:#fff;color:#475569;font-size:13px;cursor:pointer;display:flex;align-items:center;justify-content:center;transition:all .15s }
.hrmis-page-btn:hover:not(:disabled) { background:#f1f5f9 }
.hrmis-page-btn:disabled { opacity:.4;cursor:not-allowed }

/* Slide-in Panel */
.hrmis-overlay { position:fixed;inset:0;background:rgba(0,0,0,.4);z-index:1040 }
.hrmis-panel { position:fixed;top:0;right:-520px;width:500px;height:100vh;background:#fff;z-index:1050;display:flex;flex-direction:column;transition:right .3s cubic-bezier(.4,0,.2,1);box-shadow:-8px 0 40px rgba(0,0,0,.15) }
.hrmis-panel-open { right:0!important }
.hrmis-panel-header { background:linear-gradient(135deg,#059669,#0d9488);padding:20px 24px;display:flex;align-items:center;justify-content:space-between;gap:14px;flex-shrink:0 }
.hrmis-panel-title-wrap { display:flex;align-items:center;gap:14px }
.hrmis-panel-icon { width:46px;height:46px;border-radius:12px;background:rgba(255,255,255,.2);display:flex;align-items:center;justify-content:center;font-size:22px;color:#fff;flex-shrink:0 }
.hrmis-panel-title { margin:0;font-size:17px;font-weight:700;color:#fff }
.hrmis-panel-subtitle { margin:3px 0 0;font-size:12px;color:rgba(255,255,255,.75) }
.hrmis-panel-close { background:rgba(255,255,255,.15);border:none;color:#fff;width:34px;height:34px;border-radius:8px;font-size:20px;display:flex;align-items:center;justify-content:center;cursor:pointer;flex-shrink:0;transition:background .15s }
.hrmis-panel-close:hover { background:rgba(255,255,255,.3) }
.hrmis-panel-body { flex:1;overflow-y:auto }
.hrmis-panel-footer { display:flex;align-items:center;justify-content:space-between;padding:14px 20px;background:#fff;border-top:1px solid #e2e8f0;flex-shrink:0;gap:10px }

/* Form */
.hrmis-form-section { padding:20px }
.hrmis-field { display:flex;flex-direction:column;gap:6px;margin-bottom:16px }
.hrmis-field:last-child { margin-bottom:0 }
.hrmis-label { font-size:12px;font-weight:700;color:#374151 }
.hrmis-req { color:#ef4444 }
.hrmis-input { width:100%;padding:9px 12px;border:1.5px solid #e2e8f0;border-radius:8px;font-size:13px;color:#1e293b;background:#fff;outline:none;box-sizing:border-box;transition:border-color .15s }
.hrmis-input:focus { border-color:#059669;box-shadow:0 0 0 3px rgba(5,150,105,.1) }
.hrmis-input:disabled { background:#f8fafc;color:#94a3b8;cursor:not-allowed }
.hrmis-select { width:100%;padding:9px 12px;border:1.5px solid #e2e8f0;border-radius:8px;font-size:13px;color:#1e293b;background:#fff;outline:none;cursor:pointer;transition:border-color .15s }
.hrmis-select:focus { border-color:#059669 }
.hrmis-textarea { width:100%;padding:9px 12px;border:1.5px solid #e2e8f0;border-radius:8px;font-size:13px;color:#1e293b;background:#fff;outline:none;resize:vertical;box-sizing:border-box;transition:border-color .15s }
.hrmis-textarea:focus { border-color:#059669 }
.hrmis-input-icon-wrap { position:relative }
.hrmis-input-icon { position:absolute;left:9px;top:50%;transform:translateY(-50%);font-size:15px;color:#94a3b8;pointer-events:none }
.hrmis-input-icon-pad { padding-left:30px }
.hrmis-suggestions { max-height:220px;overflow-y:auto;background:#fff;border:1.5px solid #e2e8f0;border-radius:8px;margin-top:4px;box-shadow:0 4px 12px rgba(0,0,0,.1) }
.hrmis-suggestion-item { display:block;width:100%;text-align:left;padding:10px 14px;border:0;border-bottom:1px solid #f1f5f9;background:#fff;cursor:pointer;font-size:13px;transition:background .15s }
.hrmis-suggestion-item:last-child { border-bottom:none }
.hrmis-suggestion-item:hover { background:#f0fdf4 }
.hrmis-suggestion-name { display:block;font-weight:600;color:#1e293b }
.hrmis-suggestion-meta { display:block;font-size:11px;color:#94a3b8;margin-top:2px }
.hrmis-selected-info { display:flex;align-items:center;gap:6px;padding:8px 12px;background:#f0fdf4;border-radius:8px;font-size:12px;font-weight:600;color:#059669;margin-top:6px }
.hrmis-file-drop { display:flex;flex-direction:column;align-items:center;justify-content:center;gap:6px;padding:24px;border:2px dashed #cbd5e1;border-radius:10px;cursor:pointer;background:#f8fafc;transition:all .15s;font-size:13px;color:#64748b }
.hrmis-file-drop:hover { border-color:#059669;background:#f0fdf4;color:#059669 }
.hrmis-file-icon { font-size:28px }

@media(max-width:600px) { .hrmis-panel { width:100%;right:-100% } .hrmis-toolbar { flex-direction:column;align-items:stretch } }
</style>
